import { Component, ElementRef, ViewChild, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { PageShellComponent } from '@shared/components/page-shell/page-shell.component';
import { Event } from './models/event.model';
import { EventosService } from './eventos.service';

@Component({
  selector: 'app-eventos-page',
  standalone: true,
  imports: [PageShellComponent, TranslocoPipe],
  templateUrl: './eventos.page.html',
})
export class EventosPage {
  private readonly service = inject(EventosService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly transloco = inject(TranslocoService);

  @ViewChild('featuredSection')
  private featuredSection?: ElementRef<HTMLElement>;

  protected readonly breadcrumb = [{ labelKey: 'nav.events' }];
  protected readonly playlistUrl =
    'https://www.youtube.com/playlist?list=PLCDO8oMmhbxul-S14smDZaAWpkU82pJWa';
  protected readonly events = toSignal(this.service.getEvents(), {
    initialValue: [] as readonly Event[],
  });
  private readonly selected = signal<Event | null>(null);
  protected readonly featured = computed(() => this.selected() ?? this.events()[0] ?? null);
  protected readonly featuredVideoUrl = computed<SafeResourceUrl | null>(() => {
    const event = this.featured();
    if (!event) return null;

    const videoId = new URL(event.url).searchParams.get('v');
    return videoId
      ? this.sanitizer.bypassSecurityTrustResourceUrl(
          `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1`,
        )
      : null;
  });

  protected selectEvent(event: Event): void {
    this.selected.set(event);
    this.featuredSection?.nativeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }

  protected isSelected(event: Event): boolean {
    return this.featured()?.url === event.url;
  }

  protected formatDate(createdAt: string): string {
    const locale = this.transloco.getActiveLang() === 'en' ? 'en' : 'pt-BR';
    return new Intl.DateTimeFormat(locale, {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(createdAt));
  }
}
